import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-600', REQUESTED: 'bg-blue-100 text-blue-700', CONFIRMED: 'bg-emerald-100 text-emerald-700',
  ADJUSTED: 'bg-amber-100 text-amber-700', DECLINED: 'bg-red-100 text-red-700', FINALIZED: 'bg-purple-100 text-purple-700',
};
export const when = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—');

/**
 * 10/27: the send / resend machinery shared by the Syndications dashboard ("Offers out for confirmation") and the
 * Needs Syndication page. One path: POST /syndication-offer-email/send, which reuses each invitee's personal link.
 * `row` needs { deal_id, deal_id_number, deal_name, invitations? }.
 */
export function useOfferSend({ onDone }) {
  const { getAuthHeaders } = useAuth();
  const headers = useCallback(() => ({ ...getAuthHeaders(), 'X-Tenant': 'live' }), [getAuthHeaders]);
  const [busyKey, setBusyKey] = useState(null);
  const [sendFor, setSendFor] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [picked, setPicked] = useState([]);

  const send = async (row, contactIds, label) => {
    if (!contactIds.length) { toast.error('Nobody to send to'); return; }
    setBusyKey(row.deal_id);
    try {
      const r = await axios.post(`${API}/syndication-offer-email/send`, { deal_id: row.deal_id, recipient_contact_ids: contactIds }, { headers: headers() });
      const sent = r.data?.sent || 0;
      const failed = (r.data?.results || []).filter((x) => !x.success);
      if (sent) toast.success(`${label}: emailed ${sent} syndicator${sent === 1 ? '' : 's'} on #${row.deal_id_number}`);
      failed.forEach((f) => toast.error(`${f.name || f.contact_id}: ${f.error || 'failed'}`));
      onDone && (await onDone());
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Send failed');
    } finally { setBusyKey(null); }
  };

  const openSendDialog = async (row) => {
    setSendFor(row);
    try {
      const r = await axios.get(`${API}/syndication-offer-email/recipients`, { headers: headers() });
      const list = Array.isArray(r.data) ? r.data : [];
      setRecipients(list);
      const existing = new Set((row.invitations || []).map((i) => i.syndicator_id));
      setPicked(list.filter((x) => existing.has(x.id)).map((x) => x.id));
    } catch { setRecipients([]); setPicked([]); }
  };
  const resendUnanswered = (row) => send(row, (row.invitations || []).filter((i) => i.unanswered).map((i) => i.syndicator_id), 'Resent');

  return { busyKey, send, openSendDialog, resendUnanswered, dialogProps: { sendFor, setSendFor, recipients, picked, setPicked, send, busyKey } };
}

export function SendOfferDialog({ sendFor, setSendFor, recipients, picked, setPicked, send, busyKey }) {
  return (
    <Dialog open={!!sendFor} onOpenChange={(o) => { if (!o) setSendFor(null); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Send the syndication offer — #{sendFor?.deal_id_number} {sendFor?.deal_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Investors with an email on file. Already-invited syndicators are pre-checked; each gets their own confirm / adjust / decline link (unchanged on a resend).</p>
        <div className="max-h-72 overflow-y-auto space-y-1 border rounded p-2">
          {recipients.length === 0 && <p className="text-sm text-slate-400">Loading investors…</p>}
          {recipients.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer">
              <Checkbox checked={picked.includes(r.id)} onCheckedChange={(v) => setPicked((p) => (v ? [...new Set([...p, r.id])] : p.filter((x) => x !== r.id)))} />
              <span className="flex-1">{r.company_name || r.name}</span>
              <span className="text-xs text-slate-400">{r.email}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 text-xs">
          <button type="button" className="underline text-slate-500" onClick={() => setPicked(recipients.map((r) => r.id))}>all</button>
          <button type="button" className="underline text-slate-500" onClick={() => setPicked([])}>none</button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSendFor(null)}>Cancel</Button>
          <Button disabled={!picked.length || busyKey === sendFor?.deal_id} onClick={async () => { const row = sendFor; setSendFor(null); await send(row, picked, 'Sent'); }}>
            <Send className="h-4 w-4 mr-1" /> Send to {picked.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function InviteeTable({ row, busyKey, send }) {
  const invitations = row.invitations || [];
  return (
    <Table>
      <TableHeader><TableRow><TableHead>Invitee</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Proposed</TableHead><TableHead className="text-right">Asked</TableHead><TableHead className="text-right">Final</TableHead><TableHead>Last emailed</TableHead><TableHead>Answered</TableHead><TableHead className="text-right">Resend</TableHead></TableRow></TableHeader>
      <TableBody>
        {invitations.map((i) => (
          <TableRow key={i.id}>
            <TableCell>{i.name}</TableCell>
            <TableCell className="text-xs text-slate-500">{i.email || <span className="text-red-600">no email on file</span>}</TableCell>
            <TableCell><Badge className={STATUS_COLORS[i.status] || ''}>{i.status}</Badge>{i.pending_adjustment && <span className="ml-1 text-xs text-amber-700">awaiting your approval</span>}{i.adjustment_status === 'approved' && <span className="ml-1 text-xs text-emerald-700">adjustment approved</span>}{i.adjustment_status === 'declined' && <span className="ml-1 text-xs text-slate-500">adjustment declined</span>}</TableCell>
            <TableCell className="text-right">{i.allocated_pct ? `${i.allocated_pct}%` : <span className="text-slate-400">open</span>}</TableCell>
            <TableCell className="text-right">{i.adjusted_pct != null ? `${i.adjusted_pct}%` : '—'}</TableCell>
            <TableCell className="text-right">{i.final_pct ? `${i.final_pct}%` : '—'}</TableCell>
            <TableCell className="text-xs">{i.last_emailed_at ? `${when(i.last_emailed_at)} · ×${i.email_count}` : <span className="text-red-600">never</span>}</TableCell>
            <TableCell className="text-xs">{when(i.responded_at)}</TableCell>
            <TableCell className="text-right"><Button size="sm" variant="ghost" disabled={busyKey === row.deal_id || !i.email} onClick={() => send(row, [i.syndicator_id], 'Resent')}><Send className="h-3.5 w-3.5" /></Button></TableCell>
          </TableRow>
        ))}
        {invitations.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-3">No invitees yet — use “Send to…”.</TableCell></TableRow>}
      </TableBody>
    </Table>
  );
}
